import React, { useState, useEffect } from 'react';
import {
    Modal,
    Avatar,
    Empty,
    Flex,
    Typography,
    Card,
    Space,
    Input,
    Button,
    Alert,
} from 'antd';
import {
    UserOutlined,
    CrownOutlined,
    TrophyOutlined,
    SendOutlined,
    LockOutlined,
    EditOutlined,
} from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const TYPE_CONFIG = {
    head: {
        icon: <UserOutlined />,
        label: 'Head',
        accent: '#7B61FF',
        avatarBg: '#7B61FF',
        cardBg: '#F4F2FF',
        cardBorder: '1px solid #E4DEFF',
    },
    dean: {
        icon: <CrownOutlined />,
        label: 'Dean',
        accent: '#B8860B',
        avatarBg: '#B8860B',
        cardBg: '#FFFBF0',
        cardBorder: '1px solid #F0E0B0',
    },
    vc: {
        icon: <TrophyOutlined />,
        label: 'VC',
        accent: '#C62828',
        avatarBg: '#C62828',
        cardBg: '#FFF0F0',
        cardBorder: '1px solid #F5C6C6',
    },
};

// Each role maps to the one section box they are allowed to write in
// Each role maps to the one section box they are allowed to write in
const ROLE_OWN_SECTION = {
    hod: 'head',
    dean: 'dean',
    vc: 'vc',
};

// Elevated roles — faculty with these roles cannot receive comments from same role
const ELEVATED_ROLES = ['hod', 'dean', 'vc', 'admin'];


/**
 * Returns a block reason string if commenting is not allowed, or null if allowed.
 *
 * Rules:
 *  1. Own profile → always blocked
 *  2. Section box mismatch → hod/dean/vc can only write in their own box
 *  3. Faculty has an elevated role → cannot receive comments from anyone
 *     (a dean cannot comment on another dean/vc/hod, same for others)
 *     Exception: a faculty with the same elevated role as the viewer is already
 *     blocked by rule 3 since they are elevated.
 */
const ROLE_LABELS = {
    faculty: 'Faculty',
    hod: 'Head of Department',
    dean: 'Dean',
    vc: 'Vice Chancellor',
    admin: 'Super Admin',
};

const getBlockReason = ({ type, currentUserRole, currentUserId, facultyId, facultyRole }) => {
    // 1. Own profile
    if (currentUserId && facultyId && currentUserId === facultyId) {
        return 'You cannot comment on your own profile.';
    }

    // 2. Section box — commenter must stay in their own box
    const allowedSection = ROLE_OWN_SECTION[currentUserRole];
    if (!allowedSection) {
        return 'You do not have permission to add comments.';
    }
    if (type !== allowedSection) {
        const sectionLabel = TYPE_CONFIG[type]?.label ?? type;
        const myLabel = TYPE_CONFIG[allowedSection]?.label ?? allowedSection;
        return `You can only comment in the ${myLabel} section, not the ${sectionLabel} section.`;
    }

    // 3. Faculty has the same role as the commenter → blocked
    if (facultyRole && currentUserRole && facultyRole === currentUserRole) {
        return `You cannot comment on another ${ROLE_LABELS[currentUserRole] ?? currentUserRole}'s profile.`;
    }

    // 4. Faculty has any other elevated role → blocked
    if (facultyRole && ELEVATED_ROLES.includes(facultyRole)) {
        return `You cannot comment on a ${ROLE_LABELS[facultyRole] ?? facultyRole}'s profile.`;
    }

    return null; // all checks passed
};

const CommentsModal = ({
    open,
    onClose,
    type,
    record,          // faculty row — needs record.id and record.role
    comment = null,  // string | null
    onSubmit,
    currentUserRole, // 'hod' | 'dean' | 'vc'
    currentUserId,   // userDetails.user_id from JWT
}) => {
    const [draft, setDraft] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.head;

    const blockReason = getBlockReason({
        type,
        currentUserRole,
        currentUserId,
        facultyId: record?.id,
        facultyRole: record?.role,
    });

    const isBlocked = !!blockReason;
    const isEdit = !!comment;

    useEffect(() => {
        if (open) {
            setDraft(comment ?? '');
        } else {
            setDraft('');
        }
    }, [open, comment]);

    const handleSubmit = async () => {
        if (!draft.trim() || isBlocked) return;
        setSubmitting(true);
        try {
            await onSubmit?.(draft.trim());
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            title={
                <Space>
                    {React.cloneElement(config.icon, { style: { color: config.accent } })}
                    <span>{config.label} Comment — {record?.name}</span>
                </Space>
            }
            width={480}
        >
            {/* Existing comment */}
            {comment ? (
                <Card
                    size="small"
                    style={{
                        borderRadius: 12,
                        background: config.cardBg,
                        border: config.cardBorder,
                        marginBottom: 16,
                    }}
                    bodyStyle={{ padding: 14 }}
                >
                    <Flex gap={10} align="flex-start">
                        <Avatar
                            style={{ backgroundColor: config.avatarBg, flexShrink: 0 }}
                            icon={config.icon}
                        />
                        <Flex vertical style={{ flex: 1 }}>
                            <Text strong style={{ marginBottom: 4 }}>{config.label}</Text>
                            <Paragraph style={{ margin: 0 }}>{comment}</Paragraph>
                        </Flex>
                    </Flex>
                </Card>
            ) : (
                <Empty
                    description="No comment yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* Input / blocked */}
            <Flex vertical gap={8} style={{ borderTop: '1px solid #F0F0F0', paddingTop: 14 }}>
                {isBlocked ? (
                    <Alert
                        icon={<LockOutlined />}
                        message={blockReason}
                        type="warning"
                        showIcon
                        style={{ borderRadius: 8 }}
                    />
                ) : (
                    <>
                        <Text strong style={{ fontSize: 13 }}>
                            {isEdit
                                ? <Space><EditOutlined />Edit comment</Space>
                                : 'Add a comment'
                            }
                        </Text>
                        <TextArea
                            rows={3}
                            value={draft}
                            placeholder={`Write a comment as ${config.label}...`}
                            onChange={(e) => setDraft(e.target.value)}
                            style={{ borderRadius: 10 }}
                        />
                        <Flex justify="flex-end">
                            <Button
                                type="primary"
                                icon={isEdit ? <EditOutlined /> : <SendOutlined />}
                                disabled={!draft.trim() || draft.trim() === (comment ?? '')}
                                loading={submitting}
                                onClick={handleSubmit}
                                style={{
                                    borderRadius: 8,
                                    background: config.accent,
                                    borderColor: config.accent,
                                }}
                            >
                                {isEdit ? 'Update' : 'Submit'}
                            </Button>
                        </Flex>
                    </>
                )}
            </Flex>
        </Modal>
    );
};

export default CommentsModal;